<template>
    <div class="col-lg-4" v-if="!loadingStats && getTrendData().length > 0">
        <div class="card shadow-sm border-0">
            <div class="card-header bg-gradient-info text-white">
                <h6 class="card-title mb-0">
                    <i class="fas fa-chart-line mr-2"></i>
                    Tendência dos Últimos 7 Dias
                </h6>
            </div>
            <div class="card-body" style="height: 450px;">
                <canvas
                    ref="trendChartCanvas"
                    :id="chartId"
                    height="400"
                ></canvas>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'TrendChart', // nome do componente

    props: {
        loadingStats: {
            type: Boolean,
            required: true
        },
        statsData: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            trendChart: null, // instância do Chart.js
            chartId: `trendChart_${Math.random().toString(36).substr(2, 9)}`, // ID único para o gráfico
            chartJSLoaded: false // controla se Chart.js foi carregado
        }
    },

    watch: {
        // Observa mudanças nos dados das estatísticas / Watch for changes in stats data
        statsData: {
            handler(newStats) {
                if (!this.loadingStats && newStats && newStats.trend_data && newStats.trend_data.length > 0) {
                    this.$nextTick(() => {
                        this.createTrendChart();
                    });
                }
            },
            deep: true
        },

        // Observa mudanças no estado de loading / Watch for changes in loading state
        loadingStats(loading) {
            if (!loading && this.getTrendData().length > 0) {
                this.$nextTick(() => {
                    this.createTrendChart();
                });
            }
        }
    },

    mounted() {
        // Carrega dados iniciais se disponíveis / Load initial data if available
        if (!this.loadingStats && this.getTrendData().length > 0) {
            this.loadChartJSAndCreateChart();
        }
    },

    beforeDestroy() {
        // Limpa o gráfico antes de destruir o componente / Clean up chart before destroying component
        if (this.trendChart) {
            this.trendChart.destroy();
            this.trendChart = null;
        }
    },

    methods: {
        /**
         * Obtém dados de tendência das estatísticas / Get trend data from statistics
         */
        getTrendData() {
            return this.statsData && this.statsData.trend_data || [];
        },

        /**
         * Carrega Chart.js dinamicamente / Load Chart.js dynamically
         */
        loadChartJS() {
            return new Promise((resolve) => {
                if (window.Chart) {
                    this.chartJSLoaded = true;
                    resolve();
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/3.9.1/chart.min.js';
                script.onload = () => {
                    this.chartJSLoaded = true;
                    resolve();
                };
                script.onerror = () => {
                    console.error('Erro ao carregar Chart.js');
                    resolve();
                };
                document.head.appendChild(script);
            });
        },

        /**
         * Carrega Chart.js e cria o gráfico / Load Chart.js and create chart
         */
        async loadChartJSAndCreateChart() {
            await this.loadChartJS();
            this.createTrendChart();
        },

        /**
         * Cria o gráfico de tendência / Create trend chart
         */
        async createTrendChart() {
            if (!this.$refs.trendChartCanvas || this.getTrendData().length === 0) {
                return;
            }

            // Carrega Chart.js se necessário / Load Chart.js if needed
            if (!window.Chart) {
                await this.loadChartJS();
            }

            if (!window.Chart) {
                console.error('Chart.js não pôde ser carregado');
                return;
            }

            // Destrói gráfico anterior se existir / Destroy previous chart if exists
            if (this.trendChart) {
                this.trendChart.destroy();
                this.trendChart = null;
            }

            const ctx = this.$refs.trendChartCanvas.getContext('2d');
            const trendData = this.getTrendData();

            // Configuração do gráfico / Chart configuration
            const config = {
                type: 'line',
                data: {
                    labels: trendData.map(d => d.day_name), // labels dos dias da semana
                    datasets: [
                        {
                            label: 'Total',
                            data: trendData.map(d => d.total),
                            borderColor: 'rgb(54, 162, 235)',
                            backgroundColor: 'rgba(54, 162, 235, 0.1)',
                            tension: 0.4,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            fill: false
                        },
                        {
                            label: 'Sucessos',
                            data: trendData.map(d => d.success),
                            borderColor: 'rgb(75, 192, 192)',
                            backgroundColor: 'rgba(75, 192, 192, 0.1)',
                            tension: 0.4,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            fill: false
                        },
                        {
                            label: 'Erros',
                            data: trendData.map(d => d.errors),
                            borderColor: 'rgb(255, 99, 132)',
                            backgroundColor: 'rgba(255, 99, 132, 0.1)',
                            tension: 0.4,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            fill: false
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'top',
                            labels: {
                                usePointStyle: true,
                                padding: 20
                            }
                        },
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            titleColor: 'white',
                            bodyColor: 'white',
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            borderWidth: 1,
                            callbacks: {
                                afterBody: function(context) {
                                    const dataIndex = context[0].dataIndex;
                                    const dayData = trendData[dataIndex];
                                    return `Taxa de Sucesso: ${dayData.success_rate}%`;
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            display: true,
                            title: {
                                display: true,
                                text: 'Dias da Semana'
                            },
                            grid: {
                                display: false
                            }
                        },
                        y: {
                            beginAtZero: true,
                            display: true,
                            title: {
                                display: true,
                                text: 'Quantidade'
                            },
                            grid: {
                                color: 'rgba(0, 0, 0, 0.1)'
                            }
                        }
                    },
                    interaction: {
                        mode: 'nearest',
                        axis: 'x',
                        intersect: false
                    }
                }
            };

            try {
                this.trendChart = new Chart(ctx, config);
            } catch (error) {
                console.error('Erro ao criar gráfico:', error);
            }
        }
    }
}
</script>

<style scoped>
.card-body {
    position: relative;
}

canvas {
    width: 100% !important;
    height: 400px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/IntegrationLog/TrendChart.vue